import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import '../Games.css';
import {Player} from '../common/types';
import {loadJSON, saveJSON} from '../common/storage';
import {pickRandomIndices, uid} from '../common/random';
import PlayerSetup from '../common/PlayerSetup';
import PassAndPlay from '../common/PassAndPlay';
import DiscussionTimer from '../common/DiscussionTimer';
import CategoryChips from '../common/CategoryChips';
import {useAiContent} from '../common/useAiContent';
import {hasShareHash, readShareHash} from '../common/shareLink';
import ShareLinkButton from '../common/ShareLinkButton';
import JoinRound from '../common/JoinRound';
import {CUSTOM_CATEGORY, UndercoverPair} from './undercoverPairs';

const MIN_PLAYERS = 3;
const PLAYERS_KEY = 'games_players';
const CUSTOM_PAIRS_KEY = 'undercover_custom_pairs';
const DISABLED_CATEGORIES_KEY = 'undercover_disabled_categories';

type Stage = 'setup' | 'passing' | 'linked' | 'playing' | 'reveal';
type Mode = 'local' | 'link';

interface AiPair {
    main: string;
    variant: string;
    category: string;
}

interface Round {
    pair: UndercoverPair;
    oddPlayerIds: string[];
}

interface UndercoverSharePayload {
    players: Player[];
    main: string;
    variant: string;
    oddIds: string[];
    hostId?: string;
}

const renderUndercoverCard = (player: Player, data: UndercoverSharePayload) => {
    const isOdd = data.oddIds.includes(player.id);
    const text = isOdd ? data.variant : data.main;
    return <p className="GmSituationText">{text}</p>;
};

const renderUndercoverFullReveal = (data: UndercoverSharePayload) => (
    <div style={{marginTop: 16}}>
        <p>Обычная версия: <strong>{data.main}</strong></p>
        <p>Изменённая версия: <strong>{data.variant}</strong></p>
        <div className="GmRevealList">
            {data.players.map(p => (
                <div
                    className={`GmRevealItem ${data.oddIds.includes(p.id) ? 'GmRevealItem--flagged' : ''}`}
                    key={p.id}
                >
                    {p.name} {data.oddIds.includes(p.id) ? '— шпион' : ''}
                </div>
            ))}
        </div>
    </div>
);

const UndercoverGame: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>(() => loadJSON(PLAYERS_KEY, []));
    const [customPairs, setCustomPairs] = useState<UndercoverPair[]>(() => loadJSON(CUSTOM_PAIRS_KEY, []));
    const [disabledCategories, setDisabledCategories] = useState<string[]>(() => loadJSON(DISABLED_CATEGORIES_KEY, []));
    const [newMain, setNewMain] = useState('');
    const [newVariant, setNewVariant] = useState('');
    const [oddCount, setOddCount] = useState(1);
    const [stage, setStage] = useState<Stage>('setup');
    const [round, setRound] = useState<Round | null>(null);
    const [mode, setMode] = useState<Mode>('local');
    const [meId, setMeId] = useState<string | null>(null);

    const sharedHashPresent = useMemo(() => hasShareHash(), []);
    const sharedRound = useMemo(() => readShareHash<UndercoverSharePayload>(), []);

    const aiPairsRaw = useAiContent<AiPair>('undercover-pairs.json');

    const categories = useMemo(() => {
        const set = new Set(aiPairsRaw.map(p => p.category));
        return Array.from(set);
    }, [aiPairsRaw]);

    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        aiPairsRaw.forEach(p => {
            counts[p.category] = (counts[p.category] || 0) + 1;
        });
        return counts;
    }, [aiPairsRaw]);

    const allPairs = useMemo((): UndercoverPair[] => [
        ...aiPairsRaw.map((p): UndercoverPair => ({id: `ai-${p.main}`, main: p.main, variant: p.variant, category: p.category, ai: true})),
        ...customPairs.map((p): UndercoverPair => ({...p, category: CUSTOM_CATEGORY})),
    ], [aiPairsRaw, customPairs]);

    const activePairs = useMemo(
        () => allPairs.filter(p => p.custom || !disabledCategories.includes(p.category || '')),
        [allPairs, disabledCategories]
    );

    const maxOddCount = Math.max(1, players.length - 2);

    const updatePlayers = (next: Player[]) => {
        setPlayers(next);
        saveJSON(PLAYERS_KEY, next);
    };

    const addCustomPair = () => {
        const main = newMain.trim();
        const variant = newVariant.trim();
        if (!main || !variant) return;
        const next = [...customPairs, {id: uid(), main, variant, custom: true}];
        setCustomPairs(next);
        saveJSON(CUSTOM_PAIRS_KEY, next);
        setNewMain('');
        setNewVariant('');
    };

    const removeCustomPair = (id: string) => {
        const next = customPairs.filter(p => p.id !== id);
        setCustomPairs(next);
        saveJSON(CUSTOM_PAIRS_KEY, next);
    };

    const toggleCategory = (category: string) => {
        const next = disabledCategories.includes(category)
            ? disabledCategories.filter(c => c !== category)
            : [...disabledCategories, category];
        setDisabledCategories(next);
        saveJSON(DISABLED_CATEGORIES_KEY, next);
    };

    const startRound = () => {
        if (players.length < MIN_PLAYERS || activePairs.length === 0) return;

        const pair = activePairs[Math.floor(Math.random() * activePairs.length)];
        const count = Math.min(oddCount, maxOddCount);
        const oddIndices = pickRandomIndices(players.length, count);
        const oddPlayerIds = oddIndices.map(i => players[i].id);

        setRound({pair, oddPlayerIds});
        setMeId(null);
        setStage(mode === 'link' ? 'linked' : 'passing');
    };

    const playAgain = () => {
        startRound();
    };

    const backToSetup = () => {
        setRound(null);
        setMeId(null);
        setStage('setup');
    };

    if (sharedHashPresent) {
        return (
            <div className="GmPage GmPage--noBar">
                <div className="GmContainer">
                    <div className="GmTopBar">
                        <Link to="/games" className="GmBack">← Игры</Link>
                        <h1 className="GmTitle" style={{margin: 0}}>Неизвестный шпион</h1>
                    </div>

                    {sharedRound ? (
                        <JoinRound
                            players={sharedRound.players}
                            data={sharedRound}
                            joinKey={window.location.hash}
                            excludePlayerId={sharedRound.hostId}
                            renderCard={renderUndercoverCard}
                            renderFullReveal={renderUndercoverFullReveal}
                        />
                    ) : (
                        <div className="GmCard">
                            <p className="GmHint GmHint--warn">Ссылка повреждена или устарела.</p>
                            <div className="GmBtnRow" style={{marginTop: 12}}>
                                <Link to="/games/undercover" className="GmBtn">Начать заново</Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={`GmPage ${stage === 'setup' ? '' : 'GmPage--noBar'}`}>
            <div className="GmContainer">
                <div className="GmTopBar">
                    <Link to="/games" className="GmBack">← Игры</Link>
                    <h1 className="GmTitle" style={{margin: 0}}>Неизвестный шпион</h1>
                </div>

                {stage === 'setup' && (
                    <>
                        <p className="GmSubtitle">
                            Все видят одну ситуацию. У одного или нескольких игроков она слегка изменена —
                            но сами они об этом не знают и уверены, что видят то же, что и все. Обсуждайте
                            детали и вычисляйте, у кого версия отличается.
                        </p>

                        <PlayerSetup players={players} setPlayers={updatePlayers} minPlayers={MIN_PLAYERS}/>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Сколько игроков получат изменённую версию</h3>
                            <div className="GmBtnRow">
                                {Array.from({length: maxOddCount}, (_, i) => i + 1).map(n => (
                                    <button
                                        key={n}
                                        className={`GmBtn ${oddCount === n ? 'GmBtn--primary' : ''}`}
                                        onClick={() => setOddCount(n)}
                                    >{n}</button>
                                ))}
                            </div>
                        </div>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Категории ситуаций ({activePairs.length} активно)</h3>

                            {categories.length === 0 && customPairs.length === 0 && (
                                <p className="GmHint GmHint--warn">
                                    Пока нет ни одной ситуации. Сгенерируйте пул скриптом
                                    scripts/generate-ai-content.js или добавьте свою пару ниже.
                                </p>
                            )}

                            {categories.length > 0 && (
                                <CategoryChips
                                    categories={categories}
                                    counts={categoryCounts}
                                    disabled={disabledCategories}
                                    onToggle={toggleCategory}
                                />
                            )}

                            {customPairs.length > 0 && (
                                <div className="GmList" style={{marginTop: 12}}>
                                    {customPairs.map(pair => (
                                        <div className="GmListItem" key={pair.id}>
                                            <span className="GmListItem-name">{pair.main}</span>
                                            <span className="GmCheckItem-custom">своя</span>
                                            <button className="GmIconBtn" onClick={() => removeCustomPair(pair.id)} aria-label="Удалить">✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="GmInputRow" style={{flexDirection: 'column', gap: 8, marginTop: 12}}>
                                <input
                                    className="GmInput"
                                    placeholder="Обычная версия ситуации"
                                    value={newMain}
                                    onChange={(e) => setNewMain(e.target.value)}
                                />
                                <input
                                    className="GmInput"
                                    placeholder="Изменённая версия (для шпиона)"
                                    value={newVariant}
                                    onChange={(e) => setNewVariant(e.target.value)}
                                />
                                <button className="GmBtn" onClick={addCustomPair}>Добавить пару</button>
                            </div>
                        </div>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Как играть?</h3>
                            <div className="GmBtnRow">
                                <button
                                    className={`GmBtn ${mode === 'local' ? 'GmBtn--primary' : ''}`}
                                    onClick={() => setMode('local')}
                                >
                                    На одном телефоне
                                </button>
                                <button
                                    className={`GmBtn ${mode === 'link' ? 'GmBtn--primary' : ''}`}
                                    onClick={() => setMode('link')}
                                >
                                    По ссылке
                                </button>
                            </div>
                            {mode === 'link' && (
                                <p className="GmHint" style={{marginTop: 8}}>
                                    Ты сам выберешь себя из списка игроков, а остальным нужно будет открыть
                                    ссылку и сделать то же самое.
                                </p>
                            )}
                        </div>

                        <div className="GmBottomBar">
                            <div className="GmBottomBar-inner">
                                <button
                                    className="GmBtn GmBtn--primary GmBtn--big"
                                    disabled={players.length < MIN_PLAYERS || activePairs.length === 0}
                                    onClick={startRound}
                                >
                                    {mode === 'link' ? 'Создать игру по ссылке' : 'Начать раунд'}
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'passing' && round && (
                    <PassAndPlay
                        players={players}
                        onDone={() => setStage('playing')}
                        renderCard={(player) => renderUndercoverCard(player, {players, main: round.pair.main, variant: round.pair.variant, oddIds: round.oddPlayerIds})}
                    />
                )}

                {stage === 'linked' && round && !meId && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Выбери, кто ты</h3>
                        <div className="GmVoteList">
                            {players.map(player => (
                                <button key={player.id} className="GmVoteOption" onClick={() => setMeId(player.id)}>
                                    {player.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {stage === 'linked' && round && meId && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Ты — {players.find(p => p.id === meId)?.name}</h3>
                        {renderUndercoverCard(players.find(p => p.id === meId)!, {players, main: round.pair.main, variant: round.pair.variant, oddIds: round.oddPlayerIds})}

                        <div style={{marginTop: 18}}>
                            <DiscussionTimer defaultMinutes={8}/>
                        </div>

                        <div className="GmBtnRow" style={{marginTop: 16}}>
                            <ShareLinkButton
                                payload={{players, main: round.pair.main, variant: round.pair.variant, oddIds: round.oddPlayerIds, hostId: meId}}
                                label="Скопировать ссылку для игроков"
                            />
                        </div>

                        <div className="GmBtnRow" style={{marginTop: 12}}>
                            <button className="GmBtn GmBtn--primary GmBtn--big" onClick={() => setStage('reveal')}>
                                Раскрыть карты
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'playing' && round && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Обсуждение</h3>
                        <DiscussionTimer defaultMinutes={8}/>
                        <div style={{marginTop: 18}}>
                            <button className="GmBtn GmBtn--primary GmBtn--big" onClick={() => setStage('reveal')}>
                                Раскрыть карты
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'reveal' && round && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Результат</h3>
                        <p>Обычная версия: <strong>{round.pair.main}</strong></p>
                        <p>Изменённая версия: <strong>{round.pair.variant}</strong></p>
                        <div className="GmRevealList">
                            {players.map(p => (
                                <div
                                    className={`GmRevealItem ${round.oddPlayerIds.includes(p.id) ? 'GmRevealItem--flagged' : ''}`}
                                    key={p.id}
                                >
                                    {p.name} {round.oddPlayerIds.includes(p.id) ? '— шпион' : ''}
                                </div>
                            ))}
                        </div>
                        <div className="GmBtnRow" style={{marginTop: 16}}>
                            <button className="GmBtn GmBtn--primary" onClick={playAgain}>Новый раунд</button>
                            <button className="GmBtn" onClick={backToSetup}>Изменить игроков</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UndercoverGame;
